import { AxiosError } from 'axios'
import AxiosInstance from '../components/Axios'
import { EmployeeProps } from '../interfaces/employee_type'
// import { removeCookie } from 'typescript-cookie' // Removed unused import
import { API, APIResponse } from '@/components/_Shared/API/API'
import { AUTH_USER, AUTH_LOGIN, AUTH_LOGOUT, AUTH_MICROSOFT_LOGIN, AUTH_MICROSOFT_CALLBACK } from './Endpoints/auth'
/**
 * This sends a GET request to the backend and verifies a user's authentication status
 * via the session token / cookie.
 * 
 * This is called everytime the user navigates anywhere in the app, aside from the login page
 * 
 * @returns the user's information like name, email, their assigned role, and their assigned projects and tasks
 */
export async function checkUser(): Promise<EmployeeProps> {
  try {
    const response = await AxiosInstance.get(AUTH_USER)
    return response.data['user']

  } catch (error) {
    if (error instanceof AxiosError) {
      if (error.response?.status === 401) {
        // User is not authenticated
        throw new Error('Unauthorized')
      } else if (error.response?.status === 403) {
        // 403 Forbidden
        throw new Error('Forbidden')
      } else {
        console.error("Axios error: ", error)
        throw new Error('Network Error')
      }
    } else if (error instanceof Error) {
      console.error("Error: ", error)
      throw new Error('Network Error')
    } else {
      console.error("Unknown error: ", error)
      throw new Error('Network Error')
    }
  }
}

type LoginProps = {
    email: string,
    password: string
}

export type LoginResponse = {
  access: string;
  refresh: string;
  user: {
    pk: number;
    email: string;
    first_name: string;
    last_name: string;
  };
  onboarding_completed: boolean
  role: string;
}

/**
 * This sends a POST request to the backend and logs in a user
 * This function returns a boolean representing if the login was successfully authenticated.
 * 
 * (true = success, false = failure)
 * 
 * @param email user-inputted email
 * @param password user-inputted password
 * 
 * @returns the session token
 */
export async function login({email, password }: LoginProps): Promise<APIResponse<LoginResponse>> {
    const response = await API.post<LoginResponse>(AUTH_LOGIN, {
      username: email,
      password: password
    })

    if (response.status === 200) {  
      // Store JWT tokens
      localStorage.setItem('accessToken', response.data.access);
      localStorage.setItem('refreshToken', response.data.refresh);

      // Set initial Authorization header for subsequent requests in this session
      AxiosInstance.defaults.headers['Authorization'] = `Bearer ${response.data.access}`
    }

    return response
}

/**
 * Sends a POST request to log the user out
 * 
 * This is a very weird way to log out.
 * 
 * @returns a tuple [azureLogoutUrl, statusCode], where azureLogoutUrl is the URL to redirect to after logging out from Azure AD
 * 
 * if the user didn't log in through Microsoft, then azureLogoutUrl will be an empty string
 * 
 */
export async function logout(): Promise<[string, string]> {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          await AxiosInstance.post('/api/token/blacklist/', { refresh: refreshToken }); // Replace with your blacklist endpoint
        } catch (blacklistError) {
          console.error("Failed to blacklist token:", blacklistError);
          // Continue with logout even if blacklisting fails
        }
      }

      // Call the standard logout endpoint (might handle social logout redirects etc.)
      const response = await AxiosInstance.post(AUTH_LOGOUT)

      const azureLogoutUrl = response.data.redirect_url // Assuming this remains the same

      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      delete AxiosInstance.defaults.headers['Authorization'];

      if (response.status == 200) {        
        if (azureLogoutUrl)
          return [azureLogoutUrl, "200"]
        else
          return ["", "200"]
      } else {
        return ["", String(response.status)]; // Return actual status
      }

    } catch (error) {
      console.error("Logout Error: ",error)
      // Ensure tokens are cleared even if the API call fails
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      delete AxiosInstance.defaults.headers['Authorization'];
      
      // Determine status code from error if possible
      const status = error instanceof AxiosError && error.response ? String(error.response.status) : "500";
      return ["", status] // Return error status
    }
}

/**
 * This function is used to log in a user with their Microsoft account
 * 
 * First it gets the Microsoft login URL and redirects the user to it
 * 
 * After the user logs in the MS login, the callback URL goes to the backend
 * where the user's information is verified and a session token is created
 * 
 * TODO: It returns the key, which is what the auth_token is, so all we need to do is
 * store the key in the cookie. Most likely in another function
 * 
 * @returns the status code of the request
 */
export async function microsoftLogin(): Promise<number> {
  try {
    const response = await AxiosInstance.get(AUTH_MICROSOFT_LOGIN)
    const data = response.data

    window.location.href = data.auth_url
    return response.status
  } catch (error) {
    console.error("Network Error: ", error)
    return 500
  }
}

/**
 * We need this to get the auth token given by the end of the azure_ad_callback function from the backend
 * 
 * This gets called at the AuthCallback component
 * 
 * @param token the token that is sent to the callback URL
 * @returns the status code of the request
 */
export async function get_auth_and_redirect(code: string): Promise<APIResponse<LoginResponse>> {
  try {
    // Gets the auth token from the backend
    // Assuming AUTH_MICROSOFT_CALLBACK points to the correct backend endpoint like '/api/callback/'
    const response = await API.post<LoginResponse>(AUTH_MICROSOFT_CALLBACK, {code}) // Expect LoginResponse

    // NEW CODE: Handle JWT response
    if (response.status === 200) {
        // Store JWT tokens
        localStorage.setItem('accessToken', response.data.access);
        localStorage.setItem('refreshToken', response.data.refresh);

        // Set initial Authorization header for subsequent requests in this session
        AxiosInstance.defaults.headers['Authorization'] = `Bearer ${response.data.access}`
    }

    // Return the full API response
    return response;

  } catch (error) {
    console.error("Network Error fetching auth token: ", error)
    // Rethrow or handle error appropriately, potentially returning a structured error response
    // For simplicity, returning a basic error structure, adjust as needed for your API handling
    const status = error instanceof AxiosError && error.response ? error.response.status : 500;
    const message = error instanceof AxiosError && error.response?.data?.detail ? error.response.data.detail : "Microsoft login callback failed";

    return {
      status: status,
      data: {} as LoginResponse, // Provide default empty data structure for LoginResponse
      message: message,
      error: error instanceof Error ? error : new Error(String(error)),
      operation: "ERROR" // Changed to "ERROR"
    } as APIResponse<LoginResponse>;
  }
}